import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, ChevronLeft, ChevronRight } from "lucide-react";
import { accentText } from "@/lib/text";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK = [
  { name: "Acoustic Pop", snippet: "\"…and the rain on Brigade Road knew your name before I did…\"" },
  { name: "Soul / R&B", snippet: "\"…every hoodie you stole became a verse I had to sing…\"" },
  { name: "Indie Folk", snippet: "\"…we planted our story where the Goa tides turn…\"" },
  { name: "Cinematic Ballad", snippet: "\"…Ananya, the stars learned to spell you…\"" },
  { name: "Upbeat Party", snippet: "\"…slow mornings, your laugh on loop, chai gone cold…\"" },
];

export default function Samples({ content }) {
  const genres = content?.genres?.length ? content.genres : FALLBACK;
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [sec, setSec] = useState(0);
  const [dur, setDur] = useState(0);
  const timer = useRef(null);
  const audioRef = useRef(null);

  const genre = genres[active] || FALLBACK[0];
  const audioSrc = genre.audio_path ? `${API}/files/${genre.audio_path}` : "";

  useEffect(() => {
    if (playing && !audioSrc) timer.current = setInterval(() => setSec((s) => (s + 1) % 168), 1000);
    else clearInterval(timer.current);
    return () => clearInterval(timer.current);
  }, [playing, audioSrc]);

  useEffect(() => { setActive(0); setPlaying(false); setSec(0); }, [genres]);

  // Reset whenever the song changes
  useEffect(() => {
    const a = audioRef.current;
    if (a) { a.pause(); a.currentTime = 0; }
    setPlaying(false);
    setSec(0);
    setDur(0);
  }, [active, audioSrc]);

  const go = (d) => setActive((a) => (a + d + genres.length) % genres.length);

  const togglePlay = () => {
    const a = audioRef.current;
    if (audioSrc && a) {
      if (playing) a.pause();
      else a.play().catch(() => {});
      setPlaying((p) => !p);
    } else {
      setPlaying((p) => !p);
    }
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s) % 60).padStart(2, "0")}`;

  return (
    <section id="samples" className="py-20 sm:py-28 scroll-mt-24 bg-cardalt border-y-[3px] border-ink" data-testid="samples-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-pink font-semibold mb-4">{content?.samples_eyebrow ?? "Listen first"}</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-[1.1] max-w-2xl">
            {accentText(content?.samples_heading ?? "Hear what ~custom magic~ sounds like", "text-grape")}
          </h2>
          <p className="mt-4 text-plum font-medium max-w-xl text-sm sm:text-base">
            {content?.samples_subtext ?? "Sample previews below — yours is written from scratch, from your story."}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-12 rounded-[2rem] bg-white border-[3px] border-ink shadow-[8px_8px_0_#2D1A32] p-6 sm:p-10"
        >
          <div className="flex items-start justify-between gap-4 mb-8">
            <div className="flex flex-wrap gap-2.5" data-testid="genre-tabs">
              {genres.map((g, i) => (
                <button
                  key={g.name + i}
                  data-testid={`genre-tab-${g.name.toLowerCase().replace(/[\s/]+/g, "-")}`}
                  onClick={() => setActive(i)}
                  className={`btn-pop rounded-full px-5 py-2 text-xs font-head font-bold border-2 border-ink ${i === active ? "bg-pink text-white shadow-[3px_3px_0_#2D1A32]" : "bg-cream text-plum"}`}
                >
                  {g.name}
                </button>
              ))}
            </div>
            <div className="flex gap-2.5 shrink-0">
              <button data-testid="genre-prev-btn" onClick={() => go(-1)} aria-label="Previous song"
                className="btn-pop w-11 h-11 rounded-full bg-white border-2 border-ink flex items-center justify-center shadow-[3px_3px_0_#2D1A32]">
                <ChevronLeft size={18} />
              </button>
              <button data-testid="genre-next-btn" onClick={() => go(1)} aria-label="Next song"
                className="btn-pop w-11 h-11 rounded-full bg-white border-2 border-ink flex items-center justify-center shadow-[3px_3px_0_#2D1A32]">
                <ChevronRight size={18} />
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-6">
            <button
              data-testid="sample-play-btn"
              onClick={togglePlay}
              className="btn-pop w-16 h-16 shrink-0 rounded-full bg-pink text-white border-[3px] border-ink shadow-[4px_4px_0_#2D1A32] flex items-center justify-center"
              aria-label={playing ? "Pause sample" : "Play sample"}
            >
              {playing ? <Pause size={24} fill="currentColor" /> : <Play size={24} fill="currentColor" className="ml-1" />}
            </button>
            <div className="flex-1 w-full">
              <div className="flex items-end gap-[3px] h-16" aria-hidden="true">
                {[...Array(48)].map((_, i) => (
                  <span
                    key={i}
                    className={`flex-1 rounded-full ${i % 3 === 0 ? "bg-pink" : i % 3 === 1 ? "bg-sunny" : "bg-grape"} ${playing ? "eq-bar" : "scale-y-[0.3] opacity-30"}`}
                    style={{ height: `${25 + 70 * Math.abs(Math.sin(i * 0.9 + genre.name.length))}%`, animationDelay: `${i * 0.05}s` }}
                  />
                ))}
              </div>
              <div className="mt-3 flex justify-between font-mono text-[11px] text-lilac font-medium">
                <span data-testid="sample-track-time">{fmt(sec)}</span>
                <span>{genre.name} · sample</span>
                <span>{audioSrc && dur ? fmt(dur) : "2:48"}</span>
              </div>
            </div>
          </div>
          {audioSrc && (
            <audio
              key={`${active}-${audioSrc}`}
              ref={audioRef}
              src={audioSrc}
              preload="metadata"
              onTimeUpdate={(e) => setSec(e.currentTarget.currentTime)}
              onLoadedMetadata={(e) => setDur(e.currentTarget.duration || 0)}
              onEnded={() => setPlaying(false)}
            />
          )}

          <div className="mt-8 mx-auto max-w-xl rounded-2xl bg-sunny/40 border-2 border-ink p-5 rotate-[-1deg]" data-testid="sample-lyric-snippet">
            <p className="font-display font-medium text-lg sm:text-xl text-ink text-center">{genre.snippet}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
